import {
  DataClass,
  Environment,
  Provider,
  ResourceType,
  type DBServerRecord,
  type UnifiedDBRecord,
} from '@repo/api-gw-sdk';

import type { DemoSettings } from '@/contexts/environment';

import {
  inventoryData,
  NoSqlDBResourceTypes,
} from '../inventory/inventoryData';

export const mongoSearchKeywords = [
  'movie',
  'film',
  'actor',
  'movies',
  'films',
  'actors',
];

export const mongoSearchData = (
  demoSettings: DemoSettings
): UnifiedDBRecord[] => {
  const inventory = inventoryData(demoSettings);
  const resource1 = inventory.find(
    (x) => NoSqlDBResourceTypes[demoSettings.primaryProvider] === x.resourceType
  );
  const resource2 = inventory.find(
    (x) =>
      demoSettings.primaryProvider === x.cloudProvider &&
      x.classifications?.appsDetails?.apps?.some((x) => x.name === 'MongoDB')
  );
  const resource3 = inventory.find(
    (x) => x.resourceType === ResourceType.AtlasMongodbCluster
  );

  return [
    {
      type: 'table',
      table: {
        resource: {
          cloudProvider: resource1?.cloudProvider,
          resourceId: resource1?.id,
          resourceName: resource1?.resourceName,
          resourceType: resource1?.resourceType,
          resourceGroup: resource1?.resourceGroupName,
          apps:
            demoSettings.primaryProvider === Provider.Gcp ? ['Firestore'] : [],
          environment:
            resource1?.classifications?.environmentDetails?.environment,
          dataClassification:
            resource1?.classifications?.dataClassesDetails?.dataClasses,
        },
        name: 'Films',
        rowCount: 128039,
        schemaName: 'public',
        dbName: 'ads-catalog',
        serverId: resource1?.id,
        recordType: 'table',
        columns: [
          { name: '_id', type: 'ObjectId' },
          { name: 'Film_Name', type: 'string' },
          { name: 'Release_Date', type: 'date' },
          { name: 'Genre', type: 'string' },
          { name: 'Director', type: 'string' },
          { name: 'Studio', type: 'string' },
          { name: 'Budget', type: 'number' },
          { name: 'Box_Office', type: 'number' },
          { name: 'Actors', type: 'array' },
        ],
      },
    },
    {
      type: 'table',
      table: {
        resource: {
          cloudProvider: resource2?.cloudProvider,
          resourceId: resource2?.id,
          resourceName: resource2?.resourceName,
          resourceType: resource2?.resourceType,
          resourceGroup: resource2?.resourceGroupName,
          apps: resource2?.classifications?.appsDetails?.apps?.map(
            (x) => x.name
          ),
          environment:
            resource2?.classifications?.environmentDetails?.environment,
          dataClassification:
            resource2?.classifications?.dataClassesDetails?.dataClasses,
        },
        name: 'Actors',
        rowCount: 128039,
        schemaName: 'public',
        dbName: 'prod-catalog',
        serverId: resource2?.id,
        recordType: 'table',
        columns: [
          { name: '_id', type: 'ObjectId' },
          { name: 'First_Name', type: 'string' },
          { name: 'Last_Name', type: 'string' },
          { name: 'Date_Of_Birth', type: 'date' },
        ],
      },
    },
    {
      type: 'table',
      table: {
        resource: {
          cloudProvider: resource3?.cloudProvider,
          resourceId: resource3?.id,
          resourceName: resource3?.resourceName,
          resourceType: resource3?.resourceType,
          resourceGroup: resource3?.resourceGroupName,
          apps: resource3?.classifications?.appsDetails?.apps?.map(
            (x) => x.name
          ),
          environment:
            resource3?.classifications?.environmentDetails?.environment,
          dataClassification:
            resource3?.classifications?.dataClassesDetails?.dataClasses,
        },
        name: 'reviews',
        rowCount: 128039,
        schemaName: 'public',
        dbName: 'Catalog-3',
        serverId: resource3?.id,
        recordType: 'table',
        columns: [
          { name: '_id', type: 'ObjectId' },
          { name: 'Film_ID', type: 'ObjectId' },
          { name: 'Rating', type: 'number' },
          { name: 'Review', type: 'string' },
        ],
      },
    },
  ];
};

export const mongoObjectTree = (resourceId: string): DBServerRecord => ({
  serverId: resourceId,
  serverName: 'Catalog-3',
  engineType: 'mongo',
  resource: {
    cloudProvider: Provider.MongoAtlas,
    resourceId: resourceId,
    resourceName: 'db-imdb-1',
    resourceType: ResourceType.AtlasMongodbCluster,
    resourceGroup: 'Production',
    apps: ['MongoDB'],
    environment: Environment.Prod,
    dataClassification: [DataClass.Pii],
  },
  databases: [
    {
      name: 'Catalog-3',
      schemas: [
        {
          name: '',
          tables: [
            {
              name: 'Films',
              rowCount: 128039,
              columns: [
                { name: '_id', type: 'ObjectId' },
                { name: 'Film_Name', type: 'string' },
                { name: 'Release_Date', type: 'date' },
                { name: 'Genre', type: 'string' },
                { name: 'Director', type: 'string' },
                { name: 'Studio', type: 'string' },
                { name: 'Budget', type: 'number' },
                { name: 'Box_Office', type: 'number' },
                { name: 'Actors', type: 'array' },
                { name: 'location', type: 'array' },
              ],
            },
            {
              name: 'Licenses',
              rowCount: 148221,
              columns: [
                { name: '_id', type: 'ObjectId' },
                { name: 'Regions', type: 'array' },
                { name: 'License_Start_Date', type: 'date' },
                { name: 'License_End_Date', type: 'date' },
                { name: 'Audio', type: 'string' },
                { name: 'Subtitles', type: 'string' },
                { name: 'film_id', type: 'ObjectId' },
              ],
            },
          ],
        },
      ],
    },
  ],
});

const columns: string[] = [
  '_id',
  'Film_Name',
  'Release_Date',
  'Genre',
  'Director',
  'Studio',
  'Budget',
  'Box_Office',
  'Actors',
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const records: any[][] = [
  [
    '5bd761dcae323e45a93ccff8',
    'The Matrix',
    '1999-03-31T00:00:00.000Z',
    'Action',
    'Lana Wachowski',
    'Warner Bros',
    63000000,
    463517383,
    ['5bd761dcae323e45a93acff8', '5b1761dcae323e45a93ccff8'],
  ],
  [
    '5bd761dcae323e45a93ccff9',
    'Gladiator',
    '2000-05-05T00:00:00.000Z',
    'Action',
    'Ridley Scott',
    'Universal Pictures',
    103000000,
    457640427,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccffa',
    'The Dark Knight',
    '2008-07-18T00:00:00.000Z',
    'Action',
    'Christopher Nolan',
    'Warner Bros',
    185000000,
    1004558444,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccffb',
    'The Shawshank Redemption',
    '1994-10-14T00:00:00.000Z',
    'Drama',
    'Frank Darabont',
    'Columbia Pictures',
    25000000,
    28341469,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccffc',
    'The Godfather',
    '1972-03-24T00:00:00.000Z',
    'Crime',
    'Francis Ford Coppola',
    'Paramount Pictures',
    6000000,
    245066411,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccffd',
    'Pulp Fiction',
    '1994-10-14T00:00:00.000Z',
    'Crime',
    'Quentin Tarantino',
    'Miramax Films',
    8000000,
    213928762,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccffe',
    'The Lord of the Rings: The Return of the King',
    '2003-12-17T00:00:00.000Z',
    'Adventure',
    'Peter Jackson',
    'New Line Cinema',
    94000000,
    1118888979,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccfff',
    'The Lord of the Rings: The Fellowship of the Ring',
    '2001-12-19T00:00:00.000Z',
    'Adventure',
    'Peter Jackson',
    'New Line Cinema',
    93000000,
    871530324,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccf00',
    'The Lord of the Rings: The Two Towers',
    '2002-12-18T00:00:00.000Z',
    'Adventure',
    'Peter Jackson',
    'New Line Cinema',
    94000000,
    926287400,
    ['5bd761dcae323e45a93ccff8', '5bd761dcae323e45a93ccff9'],
  ],
  [
    '5bd761dcae323e45a93ccf01',
    'The Dark Knight Rises',
    '2012-07-20T00:00:00.000Z',
    'Action',
    'Christopher Nolan',
    'Warner Bros',
    250000000,
    1081041287,
    [],
  ],
  [
    '5bd761dcae323e45a93ccf02',
    'Whiplash',
    '2014-10-15T00:00:00.000Z',
    'Drama',
    'Damien Chazelle',
    'Sony Pictures Classics',
    3300000,
    48979554,
    [],
  ],
  [
    '5bd761dcae323e45a93ccf03',
    'The Social Network',
    '2010-10-01T00:00:00.000Z',
    'Biography',
    'David Fincher',
    'Columbia Pictures',
    40000000,
    224920315,
    [],
  ],
];

export const queryData = { columns, records };
